import type React from "react"
import { JournalList } from "../../components/journal/journal-list"
import { ProjectCostSection } from "../../components/project-cost-section"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useProjectDetailContext } from "./project-detail-context"
import { ProjectEditForm } from "./project-edit-form"
import { ProjectHeader } from "./project-header"

/** A bordered content card with an optional heading. */
function Card({
	heading,
	children,
	testId,
}: {
	heading?: string
	children: React.ReactNode
	testId?: string
}) {
	return (
		<section
			data-testid={testId}
			className="rounded-xl border border-border bg-card p-4 md:p-5"
		>
			{heading ? (
				<h2 className="text-base font-bold text-foreground">{heading}</h2>
			) : null}
			{children}
		</section>
	)
}

export function ProjectDetailsTab() {
	const {
		propertyId,
		project: p,
		isOwner,
		canProposeCost,
		canMarkPaid,
		editing,
		setEditing,
		onUpdateFields,
		updateFieldsPending,
	} = useProjectDetailContext()
	const { locale } = useLocale()
	const t = getTranslations(locale)

	return (
		<div className="space-y-5">
			<ProjectHeader />
			{editing ? (
				<Card heading={t.projects.detail.editTitle}>
					<ProjectEditForm
						project={p}
						onSave={onUpdateFields}
						onDone={() => setEditing(false)}
						pending={updateFieldsPending}
					/>
				</Card>
			) : (
				<Card heading={t.projects.detail.descriptionHeading}>
					{p.description ? (
						<p className="mt-3 text-base text-foreground/80 leading-relaxed whitespace-pre-wrap">
							{p.description}
						</p>
					) : (
						<p className="mt-3 text-base text-muted-foreground/80 italic">
							{t.projects.detail.noDescription}
						</p>
					)}
					{p.estimatedExecutionCost ? (
						<p className="mt-4 text-sm text-muted-foreground">
							{t.projects.detail.estimatedExecutionCostLabel}:{" "}
							<span className="text-foreground/80 font-semibold">
								{p.estimatedExecutionCost} {p.estimatedExecutionCostCurrency}
							</span>
						</p>
					) : null}
				</Card>
			)}

			<Card>
				<ProjectCostSection
					propertyId={propertyId}
					project={p}
					canPropose={canProposeCost}
					canDecide={isOwner}
					canMarkPaid={canMarkPaid}
				/>
			</Card>

			<Card
				heading={t.projects.detail.journalHeading}
				testId="project-journal-section"
			>
				<div className="mt-3">
					<JournalList propertyId={propertyId} projectId={p.id} />
				</div>
			</Card>
		</div>
	)
}
